import React from 'react';
import {
  Box,
  Avatar,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import PageTitle from '@app/components/pageTitle/PageTitle';
import MoreIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import globalAppStore from '@app/stores/globalAppStore';

import useToggle from '@app/hooks/useToggle';
import useDropdownMenu from '@app/hooks/useDropdownMenu';
import { deleteFeedbackSite, getFeedbackSites } from '@app/api/feedback-site';
import type { IFeedbackSite } from '@app/interfaces/feedback-site.interface';
import AddFeedbackSiteButton from './components/AddFeedbackSiteButton';

function FeedbackSitesPage() {
  const [feedbackSites, setFeedbackSites] = React.useState<IFeedbackSite[]>([]);
  const [isOpenEditor, openEditor, closeEditor] = useToggle(false);
  const [selectedFeedbackSite, setSelectedFeedbackSite] = React.useState<IFeedbackSite>();
  const [anchorsList, openDropdownMenu, closeDropdownMenu, closeAllDropdownMenu] =
    useDropdownMenu();
  const [isDeletingId, setIsDeletingId] = React.useState<number>();

  const fetchFeedbackSites = React.useCallback(() => {
    getFeedbackSites().then((feedbackSites) => setFeedbackSites(feedbackSites || []));
  }, []);

  React.useEffect(() => {
    fetchFeedbackSites();
  }, [fetchFeedbackSites]);

  React.useEffect(() => {
    if (!isOpenEditor) {
      setSelectedFeedbackSite(undefined);
    }
  }, [isOpenEditor]);

  const handleEdit = (feedbackSite: IFeedbackSite) => {
    setSelectedFeedbackSite(feedbackSite);
    closeAllDropdownMenu();
    openEditor();
  };

  const handleDelete = async (feedbackSite: IFeedbackSite) => {
    globalAppStore.updateGlobalConfirmPopupState({
      isVisible: true,
      title: 'Удаление сайта отзывов',
      text: 'Вы уверены, что хотите удалить сайт отзывов?',
      subtext: '',
      confirmButtonTitle: 'Удалить',
      onConfirm: async () => {
        setIsDeletingId(feedbackSite.id);
        await deleteFeedbackSite(feedbackSite);
        fetchFeedbackSites();
        closeAllDropdownMenu();
        setIsDeletingId(undefined);
      },
    });
  };

  return (
    <div className='content'>
      <PageTitle
        title='Сайты отзывов'
        action={
          <AddFeedbackSiteButton
            feedbackSite={selectedFeedbackSite}
            isOpen={isOpenEditor}
            onOpen={openEditor}
            onClose={closeEditor}
            onSuccess={fetchFeedbackSites}
          />
        }
      />

      <Box sx={{ paddingBottom: 6 }}>
        <List disablePadding sx={{ width: '100%', bgcolor: 'background.paper' }}>
          {feedbackSites.map((feedbackSite, idx) => {
            return (
              <Box key={feedbackSite.id}>
                <ListItem sx={{ paddingLeft: 0 }}>
                  <ListItemIcon sx={{ minWidth: 48 }}>
                    <Avatar sx={{ width: 36, height: 36 }}>
                      {feedbackSite.name.slice(0, 2).toUpperCase()}
                    </Avatar>
                  </ListItemIcon>

                  <ListItemText primary={feedbackSite.name} secondary={feedbackSite.index} />

                  <ListItemSecondaryAction>
                    <IconButton
                      size='large'
                      aria-label='feedback site actions'
                      aria-controls='menu-appbar'
                      aria-haspopup='true'
                      onClick={openDropdownMenu(idx)}
                      color='inherit'
                    >
                      <MoreIcon />
                    </IconButton>

                    <Menu
                      id='menu-appbar'
                      anchorEl={anchorsList?.[idx] || null}
                      anchorOrigin={{
                        vertical: 'center',
                        horizontal: 'right',
                      }}
                      keepMounted
                      transformOrigin={{
                        vertical: 'center',
                        horizontal: 'left',
                      }}
                      open={Boolean(anchorsList?.[idx])}
                      onClose={closeDropdownMenu(-1)}
                    >
                      <MenuItem onClick={() => handleEdit(feedbackSite)}>
                        <ListItemIcon>
                          <EditIcon fontSize='small' color='primary' />
                        </ListItemIcon>
                        <ListItemText>Редактировать</ListItemText>
                      </MenuItem>

                      <Divider />

                      <MenuItem
                        onClick={() => handleDelete(feedbackSite)}
                        disabled={isDeletingId === feedbackSite.id}
                      >
                        <ListItemIcon>
                          {isDeletingId === feedbackSite.id ? (
                            <CircularProgress size={16} color='error' />
                          ) : (
                            <DeleteIcon fontSize='small' color='error' />
                          )}
                        </ListItemIcon>
                        <ListItemText primaryTypographyProps={{ color: 'error' }}>
                          Удалить
                        </ListItemText>
                      </MenuItem>
                    </Menu>
                  </ListItemSecondaryAction>
                </ListItem>

                {idx < feedbackSites.length - 1 && <Divider component='li' />}
              </Box>
            );
          })}
        </List>
      </Box>
    </div>
  );
}

export default FeedbackSitesPage;
